<template>
  <form @submit.prevent="handleSubmit" class="expense-form">
    <div class="form-field">
      <IntercomSelect
        v-model="formData.category_id"
        :label="$t('expense.category')"
        :options="categoryOptions"
        :placeholder="$t('expense.selectCategory')"
        required
        filterable
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomInput
        v-model.number="formData.amount"
        :label="$t('expense.amount')"
        type="number"
        placeholder="0.00"
        required
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomDateInput
        v-model="formData.expense_date"
        :label="$t('expense.date')"
        required
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomInput
        v-model="formData.vendor"
        :label="$t('expense.vendor')"
        placeholder="e.g., Local Market, Supplier Name"
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomInput
        v-model="formData.description"
        :label="$t('expense.description')"
        placeholder="e.g., Rice 50kg, Cooking oil 10L"
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomInput
        v-model="formData.notes"
        :label="$t('expense.notes')"
        placeholder="Optional notes"
        :disabled="loading"
      />
    </div>

    <!-- Program (optional) -->
    <div v-if="programOptions.length > 0" class="form-field">
      <IntercomSelect
        v-model="formData.program_id"
        :label="$t('expense.program')"
        :options="programOptions"
        :placeholder="$t('expense.selectProgram')"
        :disabled="loading"
      />
    </div>

    <!-- Bank Account Selection -->
    <div v-if="bankAccountOptions.length > 0" class="form-field">
      <IntercomSelect
        v-model="formData.bank_account_id"
        label="Paid from Account"
        :options="bankAccountOptions"
        placeholder="Select account"
        :disabled="loading"
      />
    </div>

    <div class="form-actions">
      <IntercomButton type="button" variant="ghost" @click="$emit('cancel')" :disabled="loading">
        {{ $t('common.cancel') }}
      </IntercomButton>
      <IntercomButton type="submit" variant="primary" :loading="loading">
        {{ $t('common.save') }}
      </IntercomButton>
    </div>
  </form>
</template>

<script setup>
import { reactive, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import {
  IntercomInput,
  IntercomDateInput,
  IntercomSelect,
  IntercomButton,
} from '@shared/components/intercom'

const { t } = useI18n()

const props = defineProps({
  expense: { type: Object, default: null },
  categories: { type: Array, default: () => [] },
  bankAccounts: { type: Array, default: () => [] },
  programs: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
})

const emit = defineEmits(['submit', 'cancel'])

const formData = reactive({
  category_id: null,
  amount: null,
  expense_date: dayjs().format('YYYY-MM-DD'),
  vendor: '',
  description: '',
  notes: '',
  bank_account_id: null,
  program_id: null,
})

const categoryOptions = computed(() =>
  props.categories.map(c => ({ value: c.id, label: c.name }))
)

const bankAccountOptions = computed(() =>
  props.bankAccounts
    .filter(a => a.is_active)
    .map(a => ({ value: a.id, label: `${a.name}${a.bank_name ? ' (' + a.bank_name + ')' : ''}${a.bank_account_number ? ' - ' + a.bank_account_number : ''}` }))
)

const programOptions = computed(() => {
  if (!props.programs || props.programs.length === 0) return []
  const options = props.programs
    .filter(p => p.is_active)
    .map(p => ({ value: p.id, label: p.name }))
  options.unshift({ value: null, label: '— None —' })
  return options
})

// Populate form when expense prop changes (edit mode)
watch(() => props.expense, (newExpense) => {
  if (newExpense) {
    formData.category_id = newExpense.category_id || null
    formData.amount = newExpense.amount || null
    formData.expense_date = newExpense.expense_date
      ? dayjs(newExpense.expense_date).format('YYYY-MM-DD')
      : dayjs().format('YYYY-MM-DD')
    formData.vendor = newExpense.vendor || ''
    formData.description = newExpense.description || ''
    formData.notes = newExpense.notes || ''
    formData.program_id = newExpense.program_id || null
  }
}, { immediate: true })

const handleSubmit = () => {
  emit('submit', { ...formData })
}
</script>

<style scoped>
.expense-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.form-field {
  display: flex;
  flex-direction: column;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-4);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-gray-200);
}
</style>
